import { Button } from '@neo/primitives/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@neo/primitives/dialog'
import { useState } from 'react'
import { useVersionView } from '../../hooks/use-version-view'
import { useSwitchVersion } from '../../queries'
import type { FlowVersion } from '../../types'
import { checkFlowUnsavedChangesForNavigation } from '../../utils'
import { formatVersionDateGroup } from './version-utils'

// Shared "make this version active" flow (server-side clone + confirm dialog), used by
// both the version-history kebab and the read-only viewing banner so the mutation and
// dialog aren't duplicated. `onActivated` runs after success (e.g. close the dropdown);
// the historical view is always exited via viewVersion(null). (SER-5591)
export const useMakeVersionActive = (phloID: string | undefined, onActivated?: () => void) => {
  const { viewVersion } = useVersionView()
  const { mutate, isPending } = useSwitchVersion()
  const [target, setTarget] = useState<FlowVersion | null>(null)

  // Reachable while editing the live flow; activating refetches and would silently
  // discard in-progress edits, so guard them before opening the confirm (no-op when
  // not editing).
  const requestMakeActive = (version: FlowVersion) =>
    checkFlowUnsavedChangesForNavigation(true, () => setTarget(version))

  const confirm = () => {
    if (!phloID || !target) return
    mutate(
      { phloID, sourceVersionId: target.id },
      {
        onSuccess: () => {
          setTarget(null)
          viewVersion(null) // land on the new active version, exit any historical view
          onActivated?.()
        },
      },
    )
  }

  const dialog = (
    <Dialog
      open={!!target}
      onOpenChange={(next) => {
        if (!next && !isPending) setTarget(null)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Make this version active?</DialogTitle>
          <DialogDescription>
            {target &&
              `New conversations will use the version from ${formatVersionDateGroup(
                target.created_at,
              )}. Conversations already in progress aren't affected.`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setTarget(null)}
            disabled={isPending}
            data-testid="button-version-switch-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={confirm}
            disabled={isPending}
            data-testid="button-version-switch-confirm"
          >
            {isPending ? 'Activating…' : 'Make this version active'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )

  return { requestMakeActive, dialog, isPending }
}
